import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "@/hooks/useAuth";
import type { CrmSection } from "@/lib/api";

import BrandLogo from "./BrandLogo";

interface AdminLayoutProps {
  children: ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  icon: string;
  section: CrmSection;
  exact?: boolean;
}

interface NotificationItem {
  id: string;
  title: string;
  meta: string;
  icon: string;
  tone: string;
}

const navItems: NavItem[] = [
  { path: "/catalog", label: "Каталог", icon: "ri-archive-line", section: "catalog" },
  { path: "/orders", label: "Заказы", icon: "ri-file-list-3-line", section: "orders" },
  { path: "/requests", label: "Заявки", icon: "ri-chat-1-line", section: "requests" },
  { path: "/analytics", label: "Аналитика", icon: "ri-bar-chart-line", section: "analytics" },
  { path: "/users", label: "Пользователи", icon: "ri-user-settings-line", section: "users" },
];

const notifications: NotificationItem[] = [
  {
    id: "payment-pending",
    title: "Есть заказы, ожидающие оплаты",
    meta: "Проверьте новые заявки в разделе заказов",
    icon: "ri-bank-card-line",
    tone: "bg-warn-bg text-warn",
  },
  {
    id: "new-orders",
    title: "Появились новые заказы с сайта",
    meta: "Откройте ленту заказов, чтобы обработать их",
    icon: "ri-shopping-bag-3-line",
    tone: "bg-info-bg text-info",
  },
  {
    id: "catalog-check",
    title: "Проверьте скрытые товары",
    meta: "Часть карточек сейчас не видна покупателям",
    icon: "ri-eye-off-line",
    tone: "bg-panel-2 text-ink-3",
  },
];

function isItemActive(pathname: string, item: NavItem) {
  return item.exact ? pathname === item.path : pathname.startsWith(item.path);
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const visibleNavItems = useMemo(
    () => navItems.filter((item) => user?.permissions.includes(item.section)),
    [user?.permissions]
  );
  const currentLabel = navItems.find((item) => isItemActive(location.pathname, item))?.label || "CRM";
  const initials = useMemo(() => {
    if (!user?.username) return "CR";
    return user.username
      .split(/[\s._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("");
  }, [user?.username]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setNotificationsOpen(false);
      }
    }

    if (!menuOpen && !notificationsOpen) return;

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen, notificationsOpen]);

  useEffect(() => {
    setMobileNavOpen(false);
    setNotificationsOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    setMobileNavOpen(false);
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-bg text-ink">
      {mobileNavOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-30 bg-black/25 md:hidden"
          onClick={() => setMobileNavOpen(false)}
          aria-label="Закрыть меню"
        />
      ) : null}

      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-72 -translate-x-full flex-col border-r border-border bg-sidebar transition-transform duration-300 md:static md:z-auto md:translate-x-0 ${
          mobileNavOpen ? "translate-x-0" : ""
        } ${collapsed ? "md:w-20" : "md:w-72"}`}
      >
        <div className="flex h-[60px] items-center gap-3 border-b border-border px-4">
          <BrandLogo />
          {!collapsed ? (
            <div className="min-w-0">
              <p className="truncate text-sm font-bold tracking-[-0.01em] text-ink">Пчелиная CRM</p>
              <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-mute">
                Рабочее пространство
              </p>
            </div>
          ) : null}
          <button
            type="button"
            onClick={() => setCollapsed((value) => !value)}
            className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-lg border border-transparent text-ink-4 transition hover:border-border hover:bg-panel hover:text-ink-2"
            aria-label={collapsed ? "Развернуть меню" : "Свернуть меню"}
          >
            <i className={collapsed ? "ri-sidebar-unfold-line" : "ri-sidebar-fold-line"} />
          </button>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
          {!collapsed ? (
            <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-mute">Разделы</p>
          ) : null}
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              className={({ isActive }) =>
                `flex items-center rounded-xl border px-3 py-2.5 text-sm transition-colors ${
                  isActive || isItemActive(location.pathname, item)
                    ? "border-amber-700 bg-gradient-to-b from-[#E08812] to-amber text-white shadow-ds-1"
                    : "border-transparent text-ink-3 hover:bg-panel hover:text-ink"
                }`
              }
            >
              <span className="flex h-5 w-5 items-center justify-center">
                <i className={`${item.icon} text-base`} />
              </span>
              {!collapsed ? <span className="ml-3 truncate">{item.label}</span> : null}
            </NavLink>
          ))}
        </nav>
      </aside>

      <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="sticky top-0 z-20 flex h-[60px] items-center gap-4 border-b border-border bg-bg/95 px-4 backdrop-blur md:px-6">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="btn-secondary px-3 py-2 md:hidden"
            aria-label="Меню"
          >
            <i className="ri-menu-line" />
          </button>

          <button
            type="button"
            className="hidden max-w-xl flex-1 items-center gap-3 rounded-xl border border-border bg-panel px-3.5 py-2 text-left text-sm text-ink-4 shadow-ds-1 transition hover:border-ink-mute md:flex"
          >
            <i className="ri-search-line text-base text-ink-3" />
            <span className="truncate">Поиск по заказам, товарам и разделам</span>
            <span className="ml-auto rounded-md border border-border bg-panel-2 px-2 py-0.5 font-mono text-[10px] text-ink-mute">
              Ctrl K
            </span>
          </button>

          <div className="ml-auto flex items-center gap-3">
            <div className="relative" ref={notificationsRef}>
              <button
                type="button"
                onClick={() => setNotificationsOpen((value) => !value)}
                className="relative inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-panel text-ink-3 shadow-ds-1 transition hover:bg-panel-2 hover:text-ink"
                aria-label="Уведомления"
                aria-expanded={notificationsOpen}
              >
                <i className="ri-notification-3-line text-base" />
                <span className="absolute -right-1 -top-1 rounded-full border border-bg bg-amber px-1.5 py-0.5 font-mono text-[10px] font-bold leading-none text-white">
                  {notifications.length}
                </span>
              </button>

              {notificationsOpen ? (
                <div className="absolute right-0 top-full z-50 mt-2 w-[320px] rounded-2xl border border-border bg-panel py-2 shadow-ds-3">
                  <div className="border-b border-border-soft px-4 pb-3 pt-2">
                    <p className="text-sm font-semibold text-ink">Уведомления</p>
                    <p className="mt-1 text-xs text-ink-4">Короткая сводка по важным рабочим событиям</p>
                  </div>
                  <div className="max-h-[320px] overflow-y-auto py-2">
                    {notifications.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          setNotificationsOpen(false);
                          navigate(item.id === "catalog-check" ? "/catalog" : "/orders");
                        }}
                        className="flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-panel-2"
                      >
                        <span className={`mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl ${item.tone}`}>
                          <i className={item.icon} />
                        </span>
                        <span>
                          <span className="block text-sm font-medium text-ink">{item.title}</span>
                          <span className="mt-1 block text-xs leading-5 text-ink-4">{item.meta}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((value) => !value)}
                className="flex items-center gap-3 rounded-full border border-border bg-panel px-2 py-1.5 shadow-ds-1 transition hover:bg-panel-2"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-[#E08812] to-amber-700 text-xs font-bold text-white">
                  {initials}
                </div>
                {user?.username ? <span className="hidden max-w-[160px] truncate text-sm font-semibold text-ink-2 sm:block">{user.username}</span> : null}
                <i className={`ri-arrow-down-s-line text-sm text-ink-mute transition-transform ${menuOpen ? "rotate-180" : ""}`} />
              </button>

              {menuOpen ? (
                <div className="absolute right-0 top-full z-50 mt-2 w-56 rounded-2xl border border-border bg-panel py-1 shadow-ds-3">
                  {user?.username ? (
                    <div className="border-b border-border-soft px-4 py-3">
                      <p className="mb-0.5 text-xs text-ink-mute">Вы вошли как</p>
                      <p className="truncate text-sm font-semibold text-ink">{user.username}</p>
                      <p className="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-amber">{user.role}</p>
                    </div>
                  ) : null}

                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-danger transition-colors hover:bg-danger-bg"
                  >
                    <i className="ri-logout-box-r-line text-base" />
                    Выйти
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-auto px-4 py-5 md:px-6 md:py-6">
          <div className="mb-5">
            <p className="section-kicker">Рабочий раздел</p>
            <h1 className="mt-1 text-2xl font-bold tracking-[-0.02em] text-ink">{currentLabel}</h1>
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}
